import { useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

const POSITION_STORAGE_KEY = 'sage_last_position';
const POSITION_TIMESTAMP_KEY = 'sage_last_position_timestamp';
const RESTORE_SESSION_KEY = 'sage_position_restored_this_session';
const MAX_POSITION_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

/**
 * Routes where position restoration is allowed (initial entry points only)
 * We only restore on truly "initial" routes like /, /auth/login, etc.
 */
const RESTORATION_ALLOWED_ROUTES = [
    '/',
    '/auth/login',
    '/auth/signup',
    '/auth/verify',
];

/**
 * Routes that should trigger position save (allowlist approach)
 * These are "deep" pages the user may want to return to
 */
const SAVEABLE_ROUTES = [
    '/category/',
    '/conversations',
    // '/dashboard' removed — legacy surface; never a restorable position
    '/profile',
    '/settings',
    '/subscription',
    '/admin',
    '/facility-admin',
    '/family-admin',
    '/education-admin',
    '/homeschool/parent',
    '/homeschool/child',
    '/church/admin',
    '/learn',
    '/church/learn',
    '/learn/session',
    '/church/session',
    '/university/',
    '/elite',
    '/prep',
];

/**
 * Routes that should NEVER be saved or restored (blocklist)
 */
const BLOCKED_ROUTES = [
    '/auth/callback',
    '/auth/reset-password',
];

/**
 * Hook to remember and restore user's last position on the site
 * Provides native app-like behavior where users return to where they left off
 */
export const usePositionMemory = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, isLoading: authLoading } = useAuth();

    // Ref to track if we've already attempted restoration in this component lifecycle
    const hasAttemptedRestore = useRef(false);

    // Ref to track the last saved path to prevent duplicate saves
    const lastPathRef = useRef<string>('');

    /**
     * Check if position has already been restored in this browser session
     */
    const hasRestoredThisSession = useCallback((): boolean => {
        try {
            return sessionStorage.getItem(RESTORE_SESSION_KEY) === 'true';
        } catch {
            return false;
        }
    }, []);

    /**
     * Mark that position restoration has occurred (or been skipped) this session
     */
    const markRestorationComplete = useCallback(() => {
        try {
            sessionStorage.setItem(RESTORE_SESSION_KEY, 'true');
        } catch {
            // Silently fail if sessionStorage is unavailable
        }
    }, []);

    /**
     * Clear the session flag (used on logout)
     */
    const clearSessionFlag = useCallback(() => {
        try {
            sessionStorage.removeItem(RESTORE_SESSION_KEY);
        } catch {
            // Silently fail
        }
    }, []);

    /**
     * Save current position to localStorage
     */
    const savePosition = useCallback(() => {
        if (!user) return;

        const currentPath = location.pathname + location.search + location.hash;

        // Check blocklist first - never save these routes
        const isBlocked = BLOCKED_ROUTES.some(route => currentPath.startsWith(route));
        if (isBlocked) {
            return;
        }

        // Only save if it's a saveable route (allowlist approach)
        const isSaveable = SAVEABLE_ROUTES.some(route => currentPath.startsWith(route));
        if (!isSaveable) {
            return;
        }

        try {
            localStorage.setItem(POSITION_STORAGE_KEY, currentPath);
            localStorage.setItem(POSITION_TIMESTAMP_KEY, Date.now().toString());
            console.log('[PositionMemory] Saved position:', currentPath);
        } catch (error) {
            console.error('[PositionMemory] Error saving position:', error);
        }
    }, [user, location.pathname, location.search, location.hash]);

    /**
     * Restore last position from localStorage
     * This should ONLY run once per browser session, on initial app load
     */
    const restorePosition = useCallback(() => {
        // Double-check: don't restore if we already did in this component lifecycle
        if (hasAttemptedRestore.current) return;

        // Check sessionStorage: don't restore if we already did in this browser session
        if (hasRestoredThisSession()) {
            hasAttemptedRestore.current = true;
            return;
        }

        if (!user || authLoading) return;

        // Mark as attempted immediately to prevent race conditions
        hasAttemptedRestore.current = true;
        markRestorationComplete();

        try {
            const savedPosition = localStorage.getItem(POSITION_STORAGE_KEY);
            const savedTimestamp = localStorage.getItem(POSITION_TIMESTAMP_KEY);

            if (!savedPosition || !savedTimestamp) {
                console.log('[PositionMemory] No saved position found');
                return;
            }

            // Check if saved position is on the blocklist - if so, clear it
            const isBlockedPosition = BLOCKED_ROUTES.some(route => savedPosition.startsWith(route));
            if (isBlockedPosition) {
                console.log('[PositionMemory] Saved position is blocked, clearing:', savedPosition);
                localStorage.removeItem(POSITION_STORAGE_KEY);
                localStorage.removeItem(POSITION_TIMESTAMP_KEY);
                return;
            }

            // Check if position is too old
            const timestamp = parseInt(savedTimestamp, 10);
            const age = Date.now() - timestamp;
            if (age > MAX_POSITION_AGE) {
                console.log('[PositionMemory] Saved position too old, clearing');
                localStorage.removeItem(POSITION_STORAGE_KEY);
                localStorage.removeItem(POSITION_TIMESTAMP_KEY);
                return;
            }

            const currentPath = location.pathname;

            // Only restore if the user is currently on a restoration-allowed route
            const isRestorationAllowed = RESTORATION_ALLOWED_ROUTES.some(route =>
                currentPath === route || currentPath === route + '/'
            );

            if (!isRestorationAllowed) {
                console.log('[PositionMemory] Not on restoration-allowed route, skipping:', currentPath);
                return;
            }

            // Don't restore to the same path
            if (savedPosition === currentPath || savedPosition === currentPath + '/') {
                console.log('[PositionMemory] Already on saved position, skipping');
                return;
            }

            console.log('[PositionMemory] Restoring position:', savedPosition);
            navigate(savedPosition, { replace: true });
        } catch (error) {
            console.error('[PositionMemory] Error restoring position:', error);
        }
    }, [user, authLoading, location.pathname, navigate, hasRestoredThisSession, markRestorationComplete]);

    // Save position when route changes (debounced)
    useEffect(() => {
        if (!user) return;

        const currentPath = location.pathname + location.search + location.hash;

        // Only save if path actually changed
        if (currentPath === lastPathRef.current) {
            return;
        }

        lastPathRef.current = currentPath;

        // Small delay to avoid saving during rapid navigation
        const timer = setTimeout(() => {
            savePosition();
        }, 1000);

        return () => clearTimeout(timer);
    }, [location.pathname, location.search, location.hash, user, savePosition]);

    // Restore position on initial mount/login (only once per session)
    useEffect(() => {
        if (!user || authLoading) return;

        // Small delay to ensure routing is settled
        const timer = setTimeout(() => {
            restorePosition();
        }, 500);

        return () => clearTimeout(timer);
    }, [user, authLoading, restorePosition]);

    // Reset session flag on logout
    useEffect(() => {
        if (!user && !authLoading) {
            hasAttemptedRestore.current = false;
            clearSessionFlag();
        }
    }, [user, authLoading, clearSessionFlag]);

    return {
        savePosition,
        restorePosition,
    };
};
